import React, { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import axiosConn from '../../axiosConn';
import './galaxyRegionControl.css';

const canAssignRegion = (player) =>
  ['admin', 'galaxyAdmin'].includes(player?.data?.player?.role);

export default function GalaxyRegionControl({
  id = 'galaxy-local-ad-region',
  value = '',
  onChange,
  disabled = false,
}) {
  const { player } = useContext(AuthContext);
  const allowed = canAssignRegion(player);
  const token = player?.token;
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    async function loadOptions() {
      try {
        const response = await axiosConn.get('galaxies/region-options', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const regions = response?.data?.data?.regions;
        if (!Array.isArray(regions)) throw new Error('Invalid region options');
        if (active) setOptions(regions);
      } catch {
        if (active)
          setError(
            'Regions could not load. Your selection is unchanged. Retry to choose a region.',
          );
      } finally {
        if (active) setLoading(false);
      }
    }
    loadOptions();
    return () => {
      active = false;
    };
  }, [allowed, token, attempt]);

  if (!allowed) return null;
  const hasCurrentOption = options.some((option) => option.slug === value);
  return (
    <div className="galaxy-region-control">
      <label htmlFor={id}>Local ad region (optional)</label>
      <p id={`${id}-help`} className="galaxy-region-control__help">
        Choose a region to prioritize its Spotlight ads in Galaxy post emails.
        Leave unassigned to keep current targeting.
      </p>
      <select
        id={id}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled || loading || Boolean(error)}
        aria-describedby={`${id}-help ${id}-status`}
      >
        <option value="">Unassigned — keep current targeting</option>
        {value && !hasCurrentOption && (
          <option value={value}>{value} (current selection)</option>
        )}
        {options.map((option) => (
          <option key={option.slug} value={option.slug}>
            {option.label}
          </option>
        ))}
      </select>
      <div
        id={`${id}-status`}
        role="status"
        className="galaxy-region-control__status"
      >
        {loading && 'Loading regions…'}
        {error && (
          <>
            <span>{error}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={() => setAttempt((current) => current + 1)}
            >
              Retry regions
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function GalaxyRegionSettings({ galaxyId, regionSlug, onSaved }) {
  const { player } = useContext(AuthContext);
  const [selection, setSelection] = useState(regionSlug || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useEffect(() => {
    setSelection(regionSlug || '');
  }, [regionSlug, galaxyId]);

  async function saveRegion() {
    if (saving) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosConn.patch(
        `galaxies/${galaxyId}`,
        { regionSlug: selection || null },
        {
          headers: { Authorization: `Bearer ${player.token}` },
        },
      );
      const updated = response?.data?.data?.data;
      if (!mounted.current) return;
      if (
        !updated ||
        !Object.prototype.hasOwnProperty.call(updated, 'regionSlug')
      ) {
        throw new Error('Missing saved region');
      }
      setSelection(updated.regionSlug || '');
      onSaved(updated.regionSlug);
      setSuccess(
        updated.regionSlug
          ? 'Region saved. Matching Spotlight ads get priority.'
          : 'Region cleared. Existing targeting is restored.',
      );
    } catch {
      if (mounted.current)
        setError(
          'Could not confirm the region save. Your selection is kept. Try again.',
        );
    } finally {
      if (mounted.current) setSaving(false);
    }
  }

  if (!canAssignRegion(player)) return null;
  return (
    <section
      className="galaxy-region-settings"
      aria-label="Galaxy email ad region"
    >
      <GalaxyRegionControl
        value={selection}
        disabled={saving}
        onChange={(value) => {
          setSelection(value);
          setError('');
          setSuccess('');
        }}
      />
      <button
        type="button"
        onClick={saveRegion}
        disabled={saving || selection === (regionSlug || '')}
      >
        {saving ? 'Saving region…' : 'Save region'}
      </button>
      {error && (
        <p role="alert" className="galaxy-region-control__status">
          {error}
        </p>
      )}
      {success && (
        <p role="status" className="galaxy-region-control__status">
          {success}
        </p>
      )}
    </section>
  );
}
